import { useCallback, useEffect, useMemo, useState } from "react";
import { responseCache } from "shared/money/responseCache";
import { apiGetSWR } from "utils/apiClient";
import { 
    calcIndividualBalances, 
    calcUserBalance,
    calcIndividualBalancesByCurrency,
    calcUserBalanceByCurrency,
    calcPairwiseDebtsByCurrency,
    simplifyDebts,
    derivedBalancesFromPairwiseDebts,
    normalizeTransactionParticipants,
} from "shared/money/balances";
import type { IndividualCurrencyBalances, CurrencyBalances, PairwiseDebt } from "@/types/Balance";
import { GroupTransaction } from "@/types/GroupTransaction";
import { toTimestamp } from "utils/dateUtils";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { useTranslation } from "shared/hooks/useTranslation";
import { UserEntry } from "@/types/Group";
import { GroupDTO } from "@/types/Group";
import { activePeopleFor, dedupePeopleById, resolveCurrentParticipantId } from "features/groups/lib/groupPeople";

// ----------------------------------
// Domain Types (adjust as needed)
// ----------------------------------

/** Group shape returned by your API. Extend if your real model includes more fields. */
export interface Group {
    _id: string;
    name: string;
    inviteToken: string;
    people: UserEntry[];
    transactions: GroupTransaction[];
    userAliases?: Record<string, string>; // Map of username to alias
    simplifyDebt?: boolean;
}

/** Hook parameters: the logged-in username and the route param for the group. */
export interface UseGroupDetailsParams {
    username: string;
    userId?: string;
    groupName: string;
}

/** Hook return type: focused, intention-revealing values for the UI. */
export interface UseGroupDetailsResult {
    selectedGroup: Group;
    transactions: ReadonlyArray<GroupTransaction>;
    expenses: ReadonlyArray<GroupTransaction>;
    settlements: ReadonlyArray<GroupTransaction>;
    individualBalances: Record<string, number>;
    userBalance: number;
    individualBalancesByCurrency: IndividualCurrencyBalances;
    userBalanceByCurrency: CurrencyBalances;
    pairwiseDebts: PairwiseDebt[];
    userAliases: Record<string, string>;
    currentParticipantId: string;
    loading: boolean;
    loadError: string;
    setSelectedGroup: (group: Group) => void;
    refetch: () => void;
}

// Stable, shared empty array to avoid allocating new [] on each render.
const EMPTY_TRANSACTIONS: ReadonlyArray<GroupTransaction> = [] as const;

function normalizeGroupPeople(group: Group): Group {
    return {
        ...group,
        people: dedupePeopleById(group.people ?? []),
    };
}

function groupsListEndpoint(username: string): string {
    return `/groups/${username}`;
}

function mapDtoToGroups(groupsDto: GroupDTO[]): Group[] {
    return (groupsDto ?? []).map((g) => ({
        _id: g._id ?? "",
        name: g.name ?? "",
        inviteToken: g.inviteToken ?? "",
        people: dedupePeopleById(g.people ?? []),
        transactions: (g.transactions as GroupTransaction[]) ?? [],
        simplifyDebt: g.simplifyDebt ?? false,
    }));
}

function pickTargetGroup(groups: Group[], routeGroupKey: string): Group | undefined {
    return groups.find((groupData) => {
        const normalizedName = (groupData?.name ?? "").replace(/\s/g, "");
        return groupData?._id === routeGroupKey || normalizedName === routeGroupKey;
    });
}

function readCachedSelectedGroup(username: string, routeGroupKey: string): Group | null {
    const raw = responseCache.get<unknown>(groupsListEndpoint(username));
    if (!Array.isArray(raw)) return null;
    const target = pickTargetGroup(mapDtoToGroups(raw as GroupDTO[]), routeGroupKey);
    return target ? normalizeGroupPeople(target) : null;
}

/**
 * Load group details for a user and compute balances.
 * This hook centralizes data fetching, selection, and derived calculations.
 */
export function useGroupDetails(params: UseGroupDetailsParams): UseGroupDetailsResult {
    const { username, userId, groupName } = params;
    const isMounted = useIsMounted();
    const { t } = useTranslation();

    // Selected group defaults to a benign, non-null object to simplify consumers.
    const [selectedGroup, setSelectedGroupState] = useState<Group>(() => {
        if (!username) {
            return {
                _id: "",
                name: t("common.loading", { defaultValue: "Loading..." }),
                inviteToken: "",
                people: [],
                transactions: [],
                simplifyDebt: false,
            };
        }
        const cached = readCachedSelectedGroup(username, groupName);
        if (cached) {
            return cached;
        }
        return {
            _id: "",
            name: t("common.loading", { defaultValue: "Loading..." }),
            inviteToken: "",
            people: [],
            transactions: [],
            simplifyDebt: false,
        };
    });
    const setSelectedGroup = useCallback((group: Group) => {
        setSelectedGroupState(normalizeGroupPeople(group));
    }, []);

    const [loading, setLoading] = useState<boolean>(() => {
        if (!username) return false;
        return !Array.isArray(responseCache.get<unknown>(groupsListEndpoint(username)));
    });
    const [loadError, setLoadError] = useState<string>("");
    const [retryVersion, setRetryVersion] = useState(0);
    const refetch = useCallback(() => setRetryVersion((version) => version + 1), []);

    useEffect(() => {
        if (!username) {
            setLoading(false);
            return;
        }

        const endpoint = groupsListEndpoint(username);
        setLoadError("");
        let cancelled = false;

        const applyGroupsDto = (groupsDto: GroupDTO[]): void => {
            const groups: Group[] = mapDtoToGroups(groupsDto ?? []);
            const targetGroup = pickTargetGroup(groups, groupName);
            if (isMounted()) {
                setSelectedGroup(
                    targetGroup ?? {
                        _id: "",
                        name: "",
                        inviteToken: "",
                        people: [],
                        transactions: [],
                        simplifyDebt: false,
                    },
                );
            }
        };

        const { stale, done, cancel } = apiGetSWR<GroupDTO[]>(endpoint, (groupsDto) => {
            applyGroupsDto(groupsDto ?? []);
        }, undefined, { isCurrent: () => !cancelled });

        if (stale !== null && Array.isArray(stale)) {
            applyGroupsDto(stale);
            setLoading(false);
        } else {
            setLoading(true);
        }

        void done
            .catch((error) => {
                console.error(error);
                if (!isMounted() || cancelled) return;
                setLoadError(t("groups.loadError", { defaultValue: "Could not load group." }));
            })
            .finally(() => {
                if (isMounted() && !cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
            cancel();
        };
    }, [username, groupName, isMounted, t, setSelectedGroup, retryVersion]);

    // Sort transactions by date (desc). Use slice() to avoid mutating source arrays.
    const transactions: ReadonlyArray<GroupTransaction> = useMemo(() => {
        const list = selectedGroup?.transactions ?? EMPTY_TRANSACTIONS;
        return list.slice().sort((a, b) => toTimestamp(b.date) - toTimestamp(a.date));
    }, [selectedGroup?.transactions]);

    const normalizedTransactions: ReadonlyArray<GroupTransaction> = useMemo(
        () => normalizeTransactionParticipants(transactions as GroupTransaction[], selectedGroup?.people ?? []),
        [transactions, selectedGroup?.people],
    );

    const currentParticipantId = useMemo(
        () => resolveCurrentParticipantId(username, userId, activePeopleFor(selectedGroup?.people ?? [])),
        [username, userId, selectedGroup?.people],
    );

    const expenses: ReadonlyArray<GroupTransaction> = useMemo(
        () => normalizedTransactions.filter(tx => tx.type !== "settlement"),
        [normalizedTransactions],
    );

    const settlements: ReadonlyArray<GroupTransaction> = useMemo(
        () => normalizedTransactions.filter(tx => tx.type === "settlement"),
        [normalizedTransactions],
    );

    const pairwiseDebts: PairwiseDebt[] = useMemo(() => {
        const raw = calcPairwiseDebtsByCurrency(normalizedTransactions as GroupTransaction[], selectedGroup?.people ?? []);
        return selectedGroup?.simplifyDebt ? simplifyDebts(raw) : raw;
    }, [normalizedTransactions, selectedGroup?.simplifyDebt, selectedGroup?.people]);

    const {
        individualBalances,
        userBalance,
        individualBalancesByCurrency,
        userBalanceByCurrency,
    } = useMemo(() => {
        if (selectedGroup?.simplifyDebt) {
            return derivedBalancesFromPairwiseDebts(pairwiseDebts, currentParticipantId);
        }
        const txs = normalizedTransactions as GroupTransaction[];
        return {
            individualBalances: calcIndividualBalances(txs, currentParticipantId),
            userBalance: calcUserBalance(txs, currentParticipantId),
            individualBalancesByCurrency: calcIndividualBalancesByCurrency(txs, currentParticipantId),
            userBalanceByCurrency: calcUserBalanceByCurrency(txs, currentParticipantId),
        };
    }, [normalizedTransactions, currentParticipantId, selectedGroup?.simplifyDebt, pairwiseDebts]);

    return {
        selectedGroup,
        setSelectedGroup,
        transactions: normalizedTransactions,
        expenses,
        settlements,
        individualBalances,
        userBalance,
        individualBalancesByCurrency,
        userBalanceByCurrency,
        pairwiseDebts,
        userAliases: selectedGroup?.userAliases ?? {},
        currentParticipantId,
        loading,
        loadError,
        refetch,
    };
}
